import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useState, useRef } from "react";
import confetti from "canvas-confetti";
import NumberFlow from "@number-flow/react";

interface PricingPlan {
  name: string;
  price: string;
  yearlyPrice: string;
  yearlyTotal?: string;
  period: string;
  features: string[];
  description: string;
  buttonText: string;
  href: string;
  isPopular: boolean;
}

interface PricingProps {
  plans: PricingPlan[];
  title?: string;
  description?: string;
  sectionLabel?: string;
  billingNote?: string;
  onPlanSelect?: (plan: PricingPlan) => void;
}

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const }
  }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

export function Pricing({
  plans,
  title = "Pricing",
  description = "All plans include full AI access.",
  sectionLabel,
  billingNote,
  onPlanSelect,
}: PricingProps) {
  const [isMonthly, setIsMonthly] = useState(true);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const switchRef = useRef<HTMLButtonElement>(null);

  const handleToggle = (checked: boolean) => {
    setIsMonthly(!checked);
    if (checked && switchRef.current) {
      const rect = switchRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;

      confetti({
        particleCount: 50,
        spread: 60,
        origin: {
          x: x / window.innerWidth,
          y: y / window.innerHeight,
        },
        colors: ["#C85A35", "#FF8A65", "#FFAB91"],
        ticks: 200,
        gravity: 1.2,
        decay: 0.94,
        startVelocity: 30,
        shapes: ["circle"],
      });
    }
  };

  return (
    <div className="py-8">
      {/* Header */}
      <div className="text-center mb-12">
        {sectionLabel && (
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="text-sm font-normal tracking-[0.15em] uppercase text-muted-foreground">{sectionLabel}</span>
          </div>
        )}
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white/95 tracking-tight leading-[1.1] max-w-3xl mx-auto">
          {title}
        </h2>
        <p className="mt-6 text-base md:text-lg font-normal text-white/50 max-w-2xl mx-auto leading-[1.75]">
          {description}
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-3 mb-16">
        <span className={cn(
          "text-sm font-medium transition-colors",
          isMonthly ? "text-white" : "text-white/70"
        )}>
          Monthly
        </span>
        <Label className="cursor-pointer">
          <Switch
            ref={switchRef as any}
            checked={!isMonthly}
            onCheckedChange={handleToggle}
            aria-label="Toggle annual billing"
            className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-white/20"
          />
        </Label>
        <span className={cn(
          "text-sm font-medium transition-colors",
          !isMonthly ? "text-white" : "text-white/70"
        )}>
          Annual
        </span>
        <span className="ml-2 text-xs font-semibold text-white bg-primary px-2 py-1 rounded-full">
          2 months free
        </span>
      </div>
      {billingNote && (
        <p className="text-center text-xs font-normal text-white/35 -mt-10 mb-16">{billingNote}</p>
      )}

      {/* Pricing Cards */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
        className="grid lg:grid-cols-3 gap-6 lg:gap-8"
      >
        {plans.map((plan, index) => (
          <motion.div 
            key={index}
            variants={fadeIn}
            className={cn(
              "relative group",
              plan.isPopular && "lg:-mt-4 lg:mb-4"
            )}
          >
            {/* Popular badge */}
            {plan.isPopular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <span className="inline-block bg-primary text-primary-foreground text-xs tracking-wide uppercase px-3 py-1 font-semibold rounded-full">
                  Recommended
                </span>
              </div>
            )}
            
            <div className={cn(
              "h-full p-6 md:p-10 rounded-2xl border transition-all duration-500 text-center landing-card flex flex-col",
              plan.isPopular 
                ? "border-primary/30" 
                : "border-[#1A1A1A] hover:border-white/20"
            )}>
              {/* Plan name */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-white/60 mb-3">
                  {plan.name}
                </h3>
                <p className="text-sm font-normal text-white/40 leading-relaxed">
                  {plan.description}
                </p>
              </div>
              
              {/* Price */}
              <div className="mb-6 pb-6 border-b border-[#1A1A1A]">
                {Number(plan.price) > 0 ? (
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl md:text-5xl font-medium tracking-tight text-white">
                      <NumberFlow
                        value={isMonthly ? Number(plan.price) : Number(plan.yearlyPrice)}
                        format={{
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }}
                        transformTiming={{
                          duration: 500,
                          easing: "ease-out",
                        }}
                        willChange
                        className="tabular-nums"
                      />
                    </span>
                    <span className="text-white/40 text-sm font-normal">/{plan.period}</span>
                  </div>
                ) : (
                  <span className="text-lg font-medium tracking-tight text-white">Custom Pricing</span>
                )}
                {plan.price !== null && (
                  <p className="text-xs font-normal text-white/30 mt-2">
                    {isMonthly
                      ? "billed monthly"
                      : `billed annually at $${plan.yearlyTotal ?? (Number(plan.yearlyPrice) * 12 > 0 ? (Number(plan.yearlyPrice) * 12).toLocaleString() : '—')}/yr`}
                  </p>
                )}
              </div>
              {plan.isPopular && (
                <p className="text-xs font-normal text-white/40 italic mb-6">Everything you need to run a high-performing bid team</p>
              )}

              {/* Features */}
              <ul className="space-y-3 mb-8 text-left flex-1">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm font-normal">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span className="text-white/50">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => onPlanSelect?.(plan)}
                className={cn(
                  "w-full group/btn inline-flex items-center justify-center gap-3 h-12 rounded-full text-sm font-medium transition-all duration-300",
                  plan.isPopular 
                    ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                    : "border border-white/20 hover:border-primary/40 hover:bg-primary/5 text-white"
                )}
              >
                {plan.buttonText}
                <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-0.5 transition-transform" />
              </button>
              {plan.isPopular && (
                <p className="text-xs font-normal text-white/35 mt-3">Most popular with growing teams</p>
              )}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

export type { PricingPlan, PricingProps };
